import React, { useCallback } from 'react'
import cn from 'classnames'
import { l, Loader, BtnV2 } from '@manychat/manyui'

import { BillingCurrency } from 'common/billing/constants/BillingCurrency'
import { usePage } from 'common/core/coreHooks'
import { analyticsService } from 'utils/services/analytics'

import SparksSvg from './assets/sparks.svg'

import cm from './ConfirmCardVerification.module.css'

interface ConfirmCardVerificationProps {
  currency: BillingCurrency
  amount: number
  isLoading?: boolean
  cancelUrl: Nullable<string>
  className?: string
  onConfirm: VoidFunction
  onCancel?: VoidFunction
}

export const ConfirmCardVerification = ({
  currency,
  amount,
  cancelUrl,
  className,
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmCardVerificationProps) => {
  usePage('Card Verification', 'PAGE_VIEW.BILLING_CARD_VERIFICATION')

  const handleVerify = useCallback(() => {
    analyticsService.sendEvent('BILLING_CARD_VERIFICATION.CLICK_VERIFY')
    onConfirm()
  }, [onConfirm])

  const handleCancel = useCallback(() => {
    analyticsService.sendEvent('BILLING_CARD_VERIFICATION.CLICK_CANCEL')
    onCancel?.()
  }, [onCancel])

  if (isLoading) {
    return (
      <div className={cn(cm.root, className)}>
        <Loader center />
      </div>
    )
  }

  return (
    <div className={cn(cm.root, className)}>
      <img className="m-b-lg" src={SparksSvg} />
      <span className="text-display-sm text-center m-b-md">
        {l.translate('Thank you for choosing Manychat!')}
      </span>
      <span className="m-b-xl text-center text-secondary">
        {l.translate(
          'We’ll need to verify your card by charging you for {price} <br/> just to make sure it’s valid.',
          { price: l.currency(amount, String(currency)) },
        )}
      </span>
      <BtnV2 variant="primary" onClick={handleVerify} className="m-b-md">
        {l.translate('Verify and continue')}
      </BtnV2>
      <a
        data-test-id="cancel-url"
        href={cancelUrl ?? ''}
        className="text-primary"
        onClick={handleCancel}
      >
        {l.translate('I don’t want to verify')}
      </a>
    </div>
  )
}
